import React, { useMemo, useState, useEffect } from 'react';
import axios from 'axios';
import api from '../services/api';
import Reveal from '../components/Reveal/Reveal';
import { formatDateInParis } from '../utils/dateTime';
import { EMAIL_INPUT_PATTERN, EMAIL_INPUT_TITLE, isValidEmailAddress } from '../utils/emailValidation';
import './Events.css';

const EVENT_BODY_MAX = 260;
const MAX_TOKEN_LEN = 60; // allow longer runs before inserting soft-breaks

const Events = () => {
  const [events, setEvents] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('all');
  const [flippedCards, setFlippedCards] = useState(new Set());
  const [showRegistrationModal, setShowRegistrationModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [registrationForm, setRegistrationForm] = useState({ user_name: '', user_email: '' });
  const [submittingRegistration, setSubmittingRegistration] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      await Promise.all([fetchCategories(), fetchEvents()]);
      setLoading(false);
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (activeCategory === 'all') return;
    const exists = categories.some((row) => row.slug === activeCategory);
    if (!exists) setActiveCategory('all');
  }, [activeCategory, categories]);

  const fetchEvents = async () => {
    try {
      const response = await api.events.getEvents({ published: 'true', limit: 100 });
      setEvents(response.data.data || []);
    } catch (error) {
      console.error('Error fetching events:', error);
      setEvents([]);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await api.events.getCategories();
      const rows = response?.data?.data || [];
      setCategories(rows);
    } catch (error) {
      console.error('Error fetching categories:', error);
      setCategories([]);
    }
  };

  const categoryLabels = useMemo(() => {
    const map = {};
    for (const row of categories) {
      map[row.slug] = row.label;
    }
    return map;
  }, [categories]);

  const getCategoryLabel = (category) => {
    return categoryLabels[category] || category;
  };

  const formatDate = (dateString) => {
    const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
    return formatDateInParis(dateString, { locale: 'fr-FR', options });
  };

  /* Title truncation omitted — show full titles and rely on wrapping */

  const truncateLongTokens = (text, maxTokenLen = MAX_TOKEN_LEN) => {
    if (!text) return '';
    const s = String(text);
    // Insert zero-width space every `maxTokenLen` characters inside long tokens so they can wrap;
    // do NOT truncate the token content.
    return s.replace(new RegExp(`(\\S{${maxTokenLen}})`, 'g'), '$1\u200B');
  }; 

  const clampText = (text, max) => {
    if (!text) return '';
    return String(text).trim().slice(0, Math.max(0, max));
  };

  const getBodyTypography = (text) => {
    const len = (String(text || '').trim()).length;

    // Heuristic sizing so up to 260 chars fits the fixed inner panel with no scrolling.
    // (We keep it simple and deterministic to avoid layout thrash across many cards.)
    if (len >= 250) return { fontSize: '0.78rem', lineHeight: 1.28 };
    if (len >= 235) return { fontSize: '0.82rem', lineHeight: 1.32 };
    if (len >= 215) return { fontSize: '0.86rem', lineHeight: 1.36 };
    return { fontSize: '0.92rem', lineHeight: 1.45 };
  };

  const apiBaseUrl = axios.defaults.baseURL || '';

  const resolveMediaUrl = (path) => {
    if (!path) return null;
    const raw = String(path).trim();
    if (!raw) return null;
    if (/^https?:\/\//i.test(raw)) return raw;
    const base = String(apiBaseUrl || '').replace(/\/$/, '');
    const suffix = raw.startsWith('/') ? raw : `/${raw}`;
    return base ? `${base}${suffix}` : suffix;
  };
  const filteredEvents = activeCategory === 'all'
    ? events
    : events.filter((evt) => evt.category === activeCategory);

  const handlePremiumEventClick = (event) => {
    setSelectedEvent(event);
    setShowRegistrationModal(true);
    setRegistrationForm({ user_name: '', user_email: '' });
  };

  const handleRegistrationSubmit = async (e) => {
    e.preventDefault();
    if (!selectedEvent) return;
    if (!isValidEmailAddress(registrationForm.user_email)) {
      alert('Veuillez saisir une adresse email valide contenant @.');
      return;
    }
    
    try {
      setSubmittingRegistration(true);
      await api.events.registerForEvent(selectedEvent.slug, registrationForm);
      alert('Registration submitted successfully! You will receive an email once it is reviewed.');
      setShowRegistrationModal(false);
      setSelectedEvent(null);
      setRegistrationForm({ user_name: '', user_email: '' });
    } catch (error) {
      console.error('Failed to register for event:', error);
      const errorMsg = error?.response?.data?.error || error?.response?.data?.message || error?.message;
      alert(`Failed to register: ${errorMsg}`);
    } finally {
      setSubmittingRegistration(false);
    }
  };

  // Helper retained for potential future use


  return (
    <div className="events-page">
      {/* Hero Section */}
      <section className="events-hero">
        <div className="hero-content">
          <h1>Événements ADEEF</h1>
          <p>Découvrez nos événements et rejoignez la communauté des étudiants internationaux</p>
        </div>
      </section>

      {/* Events Section */}
      <Reveal as="section" className="events-section" variant="up" delay={60}>
        <div className="container">
          <div className="section-card">
            <div className="events-filters">
            <button className={`filter-btn ${activeCategory === 'all' ? 'active' : ''}`} onClick={() => setActiveCategory('all')}>Tous</button>
            {categories.map((category) => (
              <button
                key={category.id || category.slug}
                className={`filter-btn ${activeCategory === category.slug ? 'active' : ''}`}
                onClick={() => setActiveCategory(category.slug)}
              >
                {category.label}
              </button>
            ))}
            </div>

            {loading ? (
              <div className="loading">
                <div className="spinner"></div>
                <p>Chargement des événements...</p>
              </div>
            ) : (
              <div className="events-grid">
                {filteredEvents.map((event) => {
                  const imgSrc = resolveMediaUrl(event.banner_url);
                  const safeTitle = truncateLongTokens(event.title, MAX_TOKEN_LEN);
                  const safeBody = truncateLongTokens(event.description, MAX_TOKEN_LEN);
                  const bodyText = clampText(safeBody, EVENT_BODY_MAX);
                  const bodyTypography = getBodyTypography(bodyText);
                  return (
                    <div
                      key={event.id}
                      className={`adeef-event-card ${flippedCards.has(event.id) ? 'flipped' : ''}`}
                      aria-label={`Événement: ${event.title}`}
                      onClickCapture={(e) => {
                        const top = document.elementFromPoint(e.clientX, e.clientY);
                        console.log('card click capture', { id: event.id, clientX: e.clientX, clientY: e.clientY, top: top && `${top.tagName}.${[...top.classList].join('.')}` });
                      }}
                      onMouseDownCapture={(e) => {
                        const top = document.elementFromPoint(e.clientX, e.clientY);
                        console.log('card mousedown capture', { id: event.id, clientX: e.clientX, clientY: e.clientY, top: top && `${top.tagName}.${[...top.classList].join('.')}` });
                      }}
                      onClick={(e) => { 
                        console.log('card clicked', { id: event.id, clientX: e.clientX, clientY: e.clientY, target: e.target, currentTarget: e.currentTarget });
                        // If the card is visually flipped (hover OR explicit state) and the click lands within the CTA area,
                        // the click may be intercepted by an overlay; detect that and handle the CTA directly.
                        const cardEl = e.currentTarget;
                        const cta = cardEl.querySelector('.adeef-event-back-action');
                        const visuallyFlipped = cardEl.matches(':hover') || flippedCards.has(event.id);
                        console.log('card clicked state', { id: event.id, visuallyFlipped, hovered: cardEl.matches(':hover'), stateFlipped: flippedCards.has(event.id) });
                        if (visuallyFlipped && cta) {
                          const rect = cta.getBoundingClientRect();
                          const x = e.clientX;
                          const y = e.clientY;
                          if (x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom) {
                            e.stopPropagation();
                            const href = cta.getAttribute && cta.getAttribute('href');
                            console.log('card handler detected visual CTA click, href=', href);

                            // Add pressed animation class so user sees the button press
                            cta.classList.add('is-pressed');
                            cta.setAttribute('aria-pressed', 'true');

                            // Use transitionend if available, fallback to timeout
                            let done = false;
                            const finish = () => {
                              if (done) return; done = true;
                              cta.classList.remove('is-pressed');
                              cta.removeAttribute('aria-pressed');
                              if (href) {
                                try {
                                  const w = window.open(href, '_blank', 'noopener,noreferrer');
                                  if (!w) console.log('window.open blocked or returned null');
                                } catch (err) {
                                  console.log('window.open failed', err);
                                }
                              } else {
                                // button variant: trigger its click handler
                                try {
                                  cta.click();
                                } catch (err) {
                                  console.log('cta.click failed', err);
                                }
                              }
                            };

                            const onTransitionEnd = (ev) => {
                              // only consider transform/box-shadow transitions
                              if (ev.propertyName && (ev.propertyName.includes('transform') || ev.propertyName.includes('box-shadow'))) {
                                finish();
                              }
                            };

                            cta.addEventListener('transitionend', onTransitionEnd, { once: true });
                            // Safety fallback in case transitionend doesn't fire
                            setTimeout(finish, 180);

                            return; // don't toggle flip when user intended to click CTA
                          }
                        }

                        // If the card is visually flipped (hovered or already state-flipped) and the click
                        // is NOT on the CTA, do nothing — don't toggle persistent state. This avoids the
                        // 'first click freezes the card' problem when users click the back face.
                        if (visuallyFlipped) {
                          console.log('click on visually flipped card outside CTA - ignoring', { id: event.id });
                          return;
                        }

                        // Otherwise toggle the flipped state
                        setFlippedCards(prev => {
                          const newSet = new Set(prev);
                          if (newSet.has(event.id)) {
                            newSet.delete(event.id);
                          } else {
                            newSet.add(event.id);
                          }
                          return newSet;
                        });
                      }}
                      onMouseDown={(e) => { console.log('card mousedown', { id: event.id, target: e.target }); }}
                    >
                      <div className="adeef-event-card-content">
                        <div className="adeef-event-card-face adeef-event-card-front">
                          <div className="adeef-event-front-content">
                            <div className="adeef-event-description">
                              <div className="adeef-event-badge">{event.category_label || getCategoryLabel(event.category)}</div>
                              <div className="adeef-event-title">
                                <p>{safeTitle}</p>
                              </div>
                              <div className="adeef-event-date">📅 {formatDate(event.start_at)}</div>
                              <div className="adeef-event-body" style={bodyTypography}>{bodyText}</div>
                            </div>
                          </div>
                        </div>

                        <div className="adeef-event-card-face adeef-event-card-back" aria-hidden="true">
                          <div className="adeef-event-back-content" onClick={(e) => { console.log('back-content clicked', { id: event.id, target: e.target, currentTarget: e.currentTarget }); }} onMouseDown={(e) => { console.log('back-content mousedown', { id: event.id, target: e.target }); }}>
                            {imgSrc ? (
                              <img className="adeef-event-back-media" src={imgSrc} alt="" onError={(e) => { console.error('Event image failed to load:', imgSrc); e.currentTarget.style.display = 'none'; }} onLoad={(e) => { e.currentTarget.style.opacity = '1'; }} />
                            ) : (
                              <div className="adeef-event-back-media adeef-event-back-media--empty" />
                            )}

                            <div className="adeef-event-badge">{event.category_label || getCategoryLabel(event.category)}</div>

                            {/* Premium events show registration button, normal events show external link */}
                            {event.event_type === 'premium' ? (
                              <button
                                type="button"
                                className="adeef-event-back-action"
                                onClickCapture={(e) => { console.log('Premium event CTA capture click', { id: event.id, target: e.target }); }}
                                onMouseDown={(e) => { console.log('Premium event CTA mousedown', { id: event.id, target: e.target }); }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  console.log('Premium event CTA clicked', { id: event.id });
                                  handlePremiumEventClick(event);
                                }}
                                aria-label={`S'inscrire: ${event.title}`}
                              >
                                <span className="adeef-event-back-action-text">En savoir plus</span>
                              </button>
                            ) : event.social_url ? (
                              <a
                                className="adeef-event-back-action"
                                href={/^https?:\/\//i.test(event.social_url) ? event.social_url : `https://${event.social_url}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClickCapture={(e) => { console.log('Event CTA capture click', { id: event.id, href: e.currentTarget.getAttribute('href'), target: e.target }); }}
                                onMouseDown={(e) => { console.log('Event CTA mousedown', { id: event.id, target: e.target }); }}
                                onClick={(e) => {
                                  // Prevent the card from reacting but allow native anchor navigation to open a new tab
                                  e.stopPropagation();
                                  const href = e.currentTarget.getAttribute('href');
                                  console.log('Event CTA clicked, href=', href);
                                  // No preventDefault: use native anchor navigation first. If popup blockers prevent the new tab,
                                  // the window.open fallback will try to open a controlled window (user gesture context preserved).
                                  try {
                                    const w = window.open(href, '_blank', 'noopener,noreferrer');
                                    if (!w) console.log('window.open was blocked or returned null');
                                  } catch (err) {
                                    console.log('window.open failed', err);
                                  }
                                }}
                                aria-label={`En savoir plus: ${event.title}`}
                              >
                                <span className="adeef-event-back-action-text">En savoir plus</span>
                              </a>
                            ) : (
                              <button
                                type="button"
                                className="adeef-event-back-action"
                                onClickCapture={(e) => { console.log('Event CTA capture click (button)', { id: event.id, target: e.target }); }}
                                onMouseDown={(e) => { console.log('Event CTA mousedown (button)', { id: event.id, target: e.target }); }}
                                onClick={(e) => { e.stopPropagation(); console.log('Event CTA button clicked', { id: event.id }); alert('No external link set for this event.'); }}
                                aria-label={`En savoir plus: ${event.title}`}
                              >
                                <span className="adeef-event-back-action-text">En savoir plus</span>
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {filteredEvents.length === 0 && !loading && (
              <div className="no-events">
                <p>Aucun événement prévu pour le moment. Revenez bientôt !</p>
              </div>
            )}
          </div>
        </div>
      </Reveal>

      {/* Premium Event Registration Modal */}
      {showRegistrationModal && selectedEvent && (
        <div className="modal-overlay" onClick={() => { setShowRegistrationModal(false); setSelectedEvent(null); }}>
          <div className="modal-content modal-wide modal-zoom-in" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <div className="modal-header">
              <h3>Inscription - {selectedEvent.title}</h3>
              <button className="modal-close" onClick={() => { setShowRegistrationModal(false); setSelectedEvent(null); }}>×</button>
            </div>
            <div className="modal-body">
              <div style={{ marginBottom: '1.5rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                  <span className="adeef-event-badge">{selectedEvent.category_label || getCategoryLabel(selectedEvent.category)}</span>
                </div>
                <p style={{ fontSize: '0.95rem', color: 'var(--text-medium)', marginBottom: '0.5rem' }}>
                  📅 {formatDate(selectedEvent.start_at)}
                </p>
                <p style={{ fontSize: '0.95rem', lineHeight: '1.6' }}>
                  {selectedEvent.description}
                </p>
              </div>

              <form onSubmit={handleRegistrationSubmit}>
                <div className="form-group">
                  <label htmlFor="reg-name">Nom complet *</label>
                  <input
                    id="reg-name"
                    type="text"
                    value={registrationForm.user_name}
                    onChange={(e) => setRegistrationForm(prev => ({ ...prev, user_name: e.target.value }))}
                    required
                    placeholder="Votre nom complet"
                    style={{ width: '100%', padding: '0.75rem', border: '1px solid #ddd', borderRadius: '5px' }}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="reg-email">Email *</label>
                  <input
                    id="reg-email"
                    type="email"
                    value={registrationForm.user_email}
                    onChange={(e) => setRegistrationForm(prev => ({ ...prev, user_email: e.target.value }))}
                    required
                    placeholder="votre.email@exemple.com"
                    pattern={EMAIL_INPUT_PATTERN}
                    title={EMAIL_INPUT_TITLE}
                    style={{ width: '100%', padding: '0.75rem', border: '1px solid #ddd', borderRadius: '5px' }}
                  />
                </div>

                <div className="modal-footer" style={{ marginTop: '1.5rem', display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => { setShowRegistrationModal(false); setSelectedEvent(null); }}
                    disabled={submittingRegistration}
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    className="btn btn-success"
                    disabled={submittingRegistration}
                  >
                    {submittingRegistration ? 'Envoi...' : 'Confirmer'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Events;
